import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

import CompanyProfile from '../../Components/CompanyProfile/SetCompanyProfile/CompanyProfile';
import AccountPendingModal from '../../Components/AccountActivation/AcountActivationModal';
import { logout } from '../../../Config/Auth';

import YearMonthSelector from '../../Components/DashboardItems/YearMonthSelector/YearMonthSelector';
import JobsOverview from '../../Components/DashboardItems/JobsOverview/JobsOverview';
import JobsTrend from '../../Components/DashboardItems/JobsTrend/JobsTrend';
import InvoicesOverview from '../../Components/DashboardItems/InvoiceOverview/InvoiceOverview';
import InvoicesTrend from '../../Components/DashboardItems/InvoiceTrend/InvoiceTrend';
import TrucksTrend from '../../Components/DashboardItems/TruckTrend/TruckTrend';
import ActiveJobs from '../../Components/DashboardItems/ActiveJobs/ActiveJobs';

import './Dashboard.scss';
import apiClient from '../../../../../Config/apiClient';

const dashboardPath = process.env.REACT_APP_TC_DASHBOARD_BASE_PATH;
const signInPath = process.env.REACT_APP_TC_SIGNIN_BASE_PATH;

const Dashboard = () => {
  const [showCompanyProfileModal, setShowCompanyProfileModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [isActivated, setIsActivated] = useState(false);

  const today = new Date();
  const fiscalIndex = (today.getMonth() + 9) % 12;
  const fiscalMonthOrder = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];
  const currentFiscalMonth = fiscalMonthOrder[fiscalIndex];
  const currentFiscalYear = today.getFullYear();

  const [selectedYear, setSelectedYear] = useState(currentFiscalYear);
  const [selectedMonth, setSelectedMonth] = useState(currentFiscalMonth);

  const [jobTrendData, setJobTrendData] = useState([]);
  const [invoiceTrendData, setInvoiceTrendData] = useState([]);
  const [truckTrendData, setTruckTrendData] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);

  const [jobSummary, setJobSummary] = useState({
    MainTitle: "Jobs Overview",
    cardDetails: [
      { cardTitle: "Total", cardContent: 0 },
      { cardTitle: "Pending", cardContent: 0 },
      { cardTitle: "Completed", cardContent: 0 },
      { cardTitle: "In Progress", cardContent: 0 },
      { cardTitle: "Cancelled", cardContent: 0 }
    ]
  });

  const [invSummary, setInvSummary] = useState({
    MainTitle: "Invoice Overview",
    cardDetails: [
      { cardTitle: "Total", cardContent: 0 },
      { cardTitle: "Paid", cardContent: 0 },
      { cardTitle: "Pending", cardContent: 0 }
    ]
  });


  //Job Overview
  useEffect(() => {
    if (!isActivated) return;

    const fetchJobSummary = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/job-summary`, {
          year: selectedYear,
          month: selectedMonth
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          const data = result.data;

          setJobSummary({
            MainTitle: "Jobs Overview (" + data.month + " " + data.year + ")",
            cardDetails: [
              { cardTitle: "Total", cardContent: data.total_jobs_applied },
              { cardTitle: "Pending", cardContent: data.jobs_pending },
              { cardTitle: "Completed", cardContent: data.jobs_completed },
              { cardTitle: "In Progress", cardContent: data.jobs_in_progress },
              { cardTitle: "Cancelled", cardContent: data.jobs_canceled }
            ]
          });
        } else {
          console.error("Job summary fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching job summary:", error);
      }
    };

    fetchJobSummary();
  }, [selectedYear, selectedMonth, isActivated]);


  //Invoice Overview
  useEffect(() => {
    if (!isActivated) return;

    const fetchInvoiceSummary = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/invoice-summary`, {
          year: selectedYear,
          month: selectedMonth
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          const data = result.data;

          setInvSummary({
            MainTitle: "Invoice Overview (" + data.month + " " + data.year + ")",
            cardDetails: [
              { cardTitle: "Total", cardContent: data.total_invoice },
              { cardTitle: "Paid", cardContent: data.invoices_paid },
              { cardTitle: "Pending", cardContent: data.invoices_pending }
            ]
          });
        } else {
          console.error("Invoice summary fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching invoice summary:", error);
      }
    };

    fetchInvoiceSummary();
  }, [selectedYear, selectedMonth, isActivated]);


  //Job Trend
  useEffect(() => {
    if (!isActivated) return;

    const fetchJobTrend = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/job-trend`, {
          year: selectedYear
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setJobTrendData(result.data);
        } else {
          console.error("Job trend fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching job trend:", error);
      }
    };

    fetchJobTrend();
  }, [selectedYear, isActivated]);


  //Invoice Trend
  useEffect(() => {
    if (!isActivated) return;

    const fetchInvoiceTrend = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/invoice-trend`, {
          year: selectedYear
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setInvoiceTrendData(result.data);
        } else {
          console.error("Invoice trend fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching invoice trend:", error);
      }
    };

    fetchInvoiceTrend();
  }, [selectedYear, isActivated]);


  //Truck Trend
  useEffect(() => {
    if (!isActivated) return;

    const fetchTruckTrend = async () => {
      try {
        const response = await apiClient.post(`${dashboardPath}/job-distribution`, {
          year: selectedYear,
          month: selectedMonth
        });

        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setTruckTrendData(result.data);
        } else {
          console.error("Truck trend fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching truck trend:", error);
      }
    };

    fetchTruckTrend();
  }, [selectedYear, selectedMonth, isActivated]);


  //Active Jobs
  useEffect(() => {
    if (!isActivated) return;

    const fetchActiveJobs = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;

        const response = await apiClient.post(`${dashboardPath}/active-jobs`, { year, month });
        const result = response.data;

        if (result.status === "SUCCESS" && result.data) {
          setActiveJobs(result.data);
        } else {
          console.error("Active jobs fetch failed:", result.message);
        }
      } catch (error) {
        console.error("Error fetching active jobs:", error);
      }
    };

    fetchActiveJobs();
  }, [isActivated]);

  useEffect(() => {
    const checkCompanyStatus = async () => {
      const token = localStorage.getItem('accessToken');
      const showPending = localStorage.getItem('showPendingAfterCreate');

      if (showPending === 'true') {
        setShowPendingModal(true);
        return;
      }

      let decoded = null;

      try {
        if (token) {
          decoded = jwtDecode(token);
        }

        if (!decoded?.company_id) {
          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const refreshResponse = await apiClient.post(`${signInPath}/refresh`, {
                refreshToken
              });
              const newAccessToken = refreshResponse.data?.accessToken;
              if (newAccessToken) {
                localStorage.setItem('accessToken', newAccessToken);
                decoded = jwtDecode(newAccessToken);
              }
            }
          } catch (err) {
            console.error('Failed to refresh token:', err);
          }
        }

        const hasCompany = !!decoded?.company_id;
        const isActivated = decoded?.initial_activated;

        setIsActivated(isActivated)

        if (!hasCompany) {
          setShowCompanyProfileModal(true);
        } else if (!isActivated) {
          setShowPendingModal(true);
        }
      } catch (err) {
        console.error('Invalid token:', err);
      }

      sessionStorage.removeItem('fromSignup');
    };

    checkCompanyStatus();
  }, []);

  return (
    <main className="full-wraper dashboard-wraper">
      <div className="main-wraper">
        <div className="mi-top">
          <YearMonthSelector
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth} />
        </div>
        <div className='graph-wrapper tcgraph-wraper'>
          <div className='graph-wrapper-row1'>
            <div className='graph-wrapper-row-col'>
              <div className='jobs-wrapper-col-row'>
                <div className='overview-row tcoverview-row'>
                  <JobsOverview jobSummary={jobSummary} />
                </div>
                <div className='trend-row'>
                  <JobsTrend data={jobTrendData} />
                </div>
              </div>
              <div className='invoices-wrapper-col-row'>
                <div className='overview-row tcoverview-row'>
                  <InvoicesOverview invSummary={invSummary} />
                </div>
                <div className='trend-row'>
                  <InvoicesTrend data={invoiceTrendData} />
                </div>
              </div>
            </div>
          </div>
          <div className='graph-wrapper-row2'>
            <div className='graph-wrapper-row-col'>
              <div className='trend-row'>
                <TrucksTrend data={truckTrendData} selectedMonth={selectedMonth} selectedYear={selectedYear}/>
              </div>
              <div className='trend-row'>
                <ActiveJobs jobs={activeJobs} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPendingModal ? (
        <AccountPendingModal onLogout={logout} />
      ) : showCompanyProfileModal ? (
        <CompanyProfile onClose={() => setShowCompanyProfileModal(false)} />
      ) : null}
    </main>
  );
};

export default Dashboard;
